import { type ChangeEvent, changeEventSchema, SseParser, sessionSchema } from '@hugo/core'
import { apiFetch, authHeaders } from './api'
import { log } from './log'
import { apiUrl, isNative } from './platform'

export type LiveStatus = 'connecting' | 'live' | 'offline'

type Handlers = {
  /**
   * Where to start: the feed cursor from when the app loaded its data (`me.cursor`). Changes made
   * after it are delivered as events even if they happened before the stream connected.
   */
  after?: string | null
  onStatus: (status: LiveStatus) => void
  onEvent: (event: ChangeEvent) => void
  /** The server no longer accepts the session: stop and let the app sign out. */
  onUnauthorized: () => void
}

/**
 * Follow the change feed over Server-Sent Events.
 * - Web: EventSource (same-origin cookie). Streams reliably in every engine; reading the stream
 *   through fetch() stalls in some (WebKit on Linux held back the body).
 * - Native: fetch streaming, because EventSource cannot send the Bearer header. iOS WKWebView
 *   (CFNetwork) streams fetch bodies correctly.
 * Both start after the cursor the app loaded its data at, and resume from the last seen seq
 * after a reconnect: no change is missed, and no blanket refetch is needed (which could race a
 * change the user just made).
 */
export function connectLive(handlers: Handlers): () => void {
  return isNative ? connectWithFetch(handlers) : connectWithEventSource(handlers)
}

function parseChange(data: string): ChangeEvent | null {
  try {
    const parsed = changeEventSchema.safeParse(JSON.parse(data))
    if (parsed.success) return parsed.data
  } catch {
    // fall through
  }
  log.warn('live: ignored malformed event')
  return null
}

function backoff(attempt: number) {
  return Math.min(15_000, 500 * 2 ** attempt) + Math.random() * 250
}

function connectWithEventSource(handlers: Handlers): () => void {
  let stopped = false
  let source: EventSource | null = null
  let lastSeq: string | null = handlers.after ?? null
  let attempt = 0
  let retryTimer: ReturnType<typeof setTimeout> | null = null

  const open = () => {
    retryTimer = null
    if (stopped) return
    handlers.onStatus('connecting')
    const url = lastSeq
      ? `/api/events/stream?after=${encodeURIComponent(lastSeq)}`
      : '/api/events/stream'
    const es = new EventSource(url)
    source = es
    es.addEventListener('ready', (event) => {
      const message = event as MessageEvent<string>
      if (message.lastEventId) lastSeq = message.lastEventId
      attempt = 0
      handlers.onStatus('live')
    })
    es.addEventListener('change', (event) => {
      const message = event as MessageEvent<string>
      if (message.lastEventId) lastSeq = message.lastEventId
      const change = parseChange(message.data)
      if (change) handlers.onEvent(change)
    })
    es.onerror = () => {
      if (stopped) return
      if (es.readyState === EventSource.CONNECTING) {
        // The browser reconnects by itself and sends Last-Event-ID.
        handlers.onStatus('connecting')
        return
      }
      // CLOSED: the server refused (e.g. 401) or the network failed for good.
      es.close()
      handlers.onStatus('offline')
      void apiFetch('/api/session', sessionSchema)
        .then(({ me }) => {
          if (stopped) return
          if (!me) {
            stopped = true
            handlers.onUnauthorized()
            return
          }
          retryTimer = setTimeout(open, backoff(attempt++))
        })
        .catch(() => {
          if (!stopped) retryTimer = setTimeout(open, backoff(attempt++))
        })
    }
  }

  open()

  return () => {
    stopped = true
    source?.close()
    if (retryTimer) clearTimeout(retryTimer)
  }
}

function connectWithFetch(handlers: Handlers): () => void {
  let stopped = false
  let controller: AbortController | null = null
  let lastSeq: string | null = handlers.after ?? null
  let attempt = 0
  let retryTimer: ReturnType<typeof setTimeout> | null = null

  const scheduleRetry = () => {
    if (stopped) return
    handlers.onStatus('offline')
    retryTimer = setTimeout(run, backoff(attempt++))
  }

  async function run() {
    retryTimer = null
    if (stopped) return
    handlers.onStatus('connecting')
    controller = new AbortController()
    try {
      const response = await fetch(apiUrl('/api/events/stream'), {
        signal: controller.signal,
        cache: 'no-store',
        credentials: 'omit',
        headers: {
          accept: 'text/event-stream',
          ...(await authHeaders()),
          ...(lastSeq ? { 'last-event-id': lastSeq } : {}),
        },
      })
      if (response.status === 401) {
        stopped = true
        handlers.onStatus('offline')
        handlers.onUnauthorized()
        return
      }
      if (!response.ok || !response.body) throw new Error(`stream failed (${response.status})`)
      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
      const parser = new SseParser()
      while (!stopped) {
        const { value, done } = await reader.read()
        if (done) break
        for (const message of parser.push(value)) {
          if (message.id) lastSeq = message.id
          if (message.event === 'ready') {
            attempt = 0
            handlers.onStatus('live')
          } else if (message.event === 'change') {
            const change = parseChange(message.data)
            if (change) handlers.onEvent(change)
          }
        }
      }
    } catch (error) {
      if (stopped) return
      log.warn(
        'live: connection lost, reconnecting',
        error instanceof Error ? error.message : error,
      )
    }
    scheduleRetry()
  }

  void run()

  return () => {
    stopped = true
    controller?.abort()
    if (retryTimer) clearTimeout(retryTimer)
  }
}
